import { useQuery } from "@tanstack/react-query";
import { CalendarPlus } from "lucide-react";
import { useDataProvider, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { useAjustes } from "../datos/useAjustes";
import { aFechaISO } from "../dominio/fechas";

/**
 * En la hoja «Quiere reunión» (S10, Robert 07/10/2026): el enlace a la página de reservas
 * «Diagnóstico NEVORA» (30 min, agenda única de Jan y Robert; lo pone Robert en Ajustes) y
 * el contador de consultorías de esa semana, con aviso al llegar al tope (5). No bloquea:
 * el hueco lo da la página de reservas; el CRM solo registra la fecha.
 */
export const ReservaConsultoria = ({
  fechaReunion,
  sinEnlace = false,
}: {
  /** Valor del campo fecha y hora (datetime-local), o vacío. */
  fechaReunion: string;
  /** 41: con el calendario propio, sin el enlace de reservas (solo el contador). */
  sinEnlace?: boolean;
}) => {
  const translate = useTranslate();
  const { ajustes } = useAjustes();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const dia = fechaReunion ? fechaReunion.slice(0, 10) : aFechaISO(new Date());
  const { data: semana } = useQuery({
    queryKey: ["nevora", "consultoriasSemana", dia],
    queryFn: () => dataProvider.consultoriasSemana(dia),
    staleTime: 30_000,
  });
  const tope = ajustes.topeConsultoriasSemana;
  const lleno = semana !== undefined && semana >= tope;

  return (
    <div className="space-y-2">
      {sinEnlace ? null : ajustes.reservasUrl ? (
        <Button asChild variant="outline" className="w-full">
          <a href={ajustes.reservasUrl} target="_blank" rel="noreferrer">
            <CalendarPlus className="size-4 mr-2" />
            {translate("nevora.reservas.abrir")}
          </a>
        </Button>
      ) : (
        <p className="text-xs text-muted-foreground">
          {translate("nevora.reservas.sin_enlace")}
        </p>
      )}
      {semana !== undefined ? (
        <p
          className={cn(
            "text-sm",
            lleno
              ? "font-medium text-amber-800 dark:text-amber-300"
              : "text-muted-foreground",
          )}
          role={lleno ? "alert" : undefined}
          data-testid="consultorias-semana"
        >
          {translate(
            lleno ? "nevora.reservas.tope" : "nevora.reservas.contador",
            {
              n: semana,
              tope,
            },
          )}
        </p>
      ) : null}
    </div>
  );
};
